import { Fragment, useEffect, useMemo, useState } from 'react';
import { DayNames, DayTime } from '../../constants/calendar.constant';
import { CalendarWeekProperties } from '../../util/calendar-properties';
import { CalendarEvent } from './Calendar';
import { isSameDate } from '../../helper/date-helper';

type WeeklyEventDisplay = {
  colStart: number;
  colStartStyling: string;
  startHour: number;
  endHour: number;
  displayWidth?: string;
  offset?: number;
} & CalendarEvent;

const detectOverlaps = (events: WeeklyEventDisplay[]) => {
  let overlaps: { [key: string]: number[] } = {};

  for (let i = 0; i < events.length; i++) {
    for (let j = i + 1; j < events.length; j++) {
      // If they share the same column and have overlapping time
      if (
        events[i].colStart === events[j].colStart &&
        ((events[i].startHour <= events[j].startHour &&
          events[i].endHour > events[j].startHour) ||
          (events[j].startHour <= events[i].startHour &&
            events[j].endHour > events[i].startHour))
      ) {
        overlaps[i] = overlaps[i] || [];
        overlaps[i].push(j);
      }
    }
  }

  return overlaps;
};

const WeekView = ({
  events,
  today,
  startOfWeek,
  currentDate,
  onUpdateCurrentDate,
}: PropsType) => {
  const { daysInWeek, isToday } = CalendarWeekProperties(today, startOfWeek);
  const [weeklyEventDisplay, setWeeklyEventDisplay] = useState<
    WeeklyEventDisplay[]
  >([]);

  const onClickDate = (dayIdx: number) => {
    const newDate = new Date(startOfWeek);
    newDate.setDate(newDate.getDate() + dayIdx);
    onUpdateCurrentDate(newDate);
  };

  const endOfWeek = useMemo(() => {
    const newDate = new Date(startOfWeek);
    newDate.setDate(newDate.getDate() + 6);
    newDate.setHours(23);
    newDate.setMinutes(59);
    newDate.setSeconds(59);
    newDate.setMilliseconds(999);
    return newDate;
  }, [startOfWeek]);

  // To filter the events based on the selected weeks
  const eventsOfTheWeek = useMemo(() => {
    return events.filter(
      (event) =>
        (event.start >= startOfWeek && event.start <= endOfWeek) ||
        (event.end >= startOfWeek && event.end <= endOfWeek) ||
        (event.start < startOfWeek && event.end > endOfWeek)
    );
  }, [events, startOfWeek, endOfWeek]);

  // To convert events into weekly display model based on the selected week range
  const convertToWeeklyViewEvent = (
    eventsOfTheWeek: CalendarEvent[],
    parsedStartOfWeek: Date,
    parsedEndOfWeek: Date
  ): WeeklyEventDisplay[] => {
    const weeklyViewEvents: WeeklyEventDisplay[] = [];

    eventsOfTheWeek.forEach((event) => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);

      let currentEventStart = eventStart;

      while (
        currentEventStart < eventEnd &&
        currentEventStart < parsedEndOfWeek
      ) {
        const currentEventEnd = new Date(currentEventStart);
        currentEventEnd.setHours(23, 59, 59, 999); // end of the day

        // Check if the event ends before the end of the day or before end of the week
        if (eventEnd < currentEventEnd || currentEventEnd > parsedEndOfWeek) {
          currentEventEnd.setTime(eventEnd.getTime());
        }

        const dayDiff = Math.floor(
          (currentEventStart.getTime() - parsedStartOfWeek.getTime()) /
            (1000 * 60 * 60 * 24)
        );
        const colStart = dayDiff + 1;

        const timeStart =
          currentEventStart.getHours() + currentEventStart.getMinutes() / 60;
        const timeEnd =
          currentEventEnd.getHours() + currentEventEnd.getMinutes() / 60;

        weeklyViewEvents.push({
          ...event,
          colStart: colStart,
          colStartStyling: `col-start-${colStart}`,
          startHour: timeStart,
          endHour: timeEnd,
          start: new Date(currentEventStart),
          end: new Date(currentEventEnd),
        });

        // Move to the next day, starting from the beginning of the day
        currentEventStart.setDate(currentEventStart.getDate() + 1);
        currentEventStart.setHours(0, 0, 0, 0);
      }
    });
    return weeklyViewEvents;
  };

  // Update weekly display to handle overlapping displasy
  useEffect(() => {
    const parsedStartOfWeek = new Date(startOfWeek);
    const parsedEndOfWeek = new Date(endOfWeek);

    const weeklyViewEvents = convertToWeeklyViewEvent(
      eventsOfTheWeek,
      parsedStartOfWeek,
      parsedEndOfWeek
    );

    const overlaps = detectOverlaps(weeklyViewEvents);

    for (let key in overlaps) {
      const mainEventIdx = parseInt(key);
      const overlappingEvents = overlaps[key];
      const totalEvents = overlappingEvents.length + 1; // +1 for the main event itself

      weeklyViewEvents[
        mainEventIdx
      ].displayWidth = `calc(100% / ${totalEvents})`;
      weeklyViewEvents[mainEventIdx].offset = 0;

      overlappingEvents.forEach((eventIdx, i) => {
        weeklyViewEvents[eventIdx].displayWidth = `calc(100% / ${totalEvents})`;
        weeklyViewEvents[eventIdx].offset = (i + 1) * (100 / totalEvents); // position the events side by side
      });
    }

    setWeeklyEventDisplay(weeklyViewEvents);
  }, [eventsOfTheWeek, startOfWeek, endOfWeek]);

  return (
    <div className="lg:flex lg:h-full lg:flex-col">
      <div className="isolate flex flex-auto flex-col overflow-auto bg-white">
        <div
          style={{ width: '165%' }}
          className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full"
        >
          <div className="sticky top-0 z-30 flex-none bg-white shadow ring-1 ring-black ring-opacity-5 sm:pr-8">
            {/* For mobile week display */}
            <div className="grid grid-cols-7 text-sm leading-6 text-gray-500 sm:hidden">
              {DayNames.map(
                (day, idx) =>
                  daysInWeek[idx] && (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        onClickDate(idx);
                      }}
                      className="flex flex-col items-center pb-3 pt-2"
                    >
                      {day.substring(0, 1)}
                      <span
                        className={`mt-1 flex h-8 w-8 items-center justify-center font-semibold  
                        ${
                          isSameDate(daysInWeek[idx], currentDate)
                            ? ' rounded-full bg-gray-500 text-white'
                            : ''
                        }
                        ${
                          isToday(daysInWeek[idx])
                            ? ' rounded-full bg-indigo-600 text-white'
                            : 'text-gray-900'
                        }`}
                      >
                        {daysInWeek[idx]?.getDate()}
                      </span>
                    </button>
                  )
              )}
            </div>

            {/* For full-screen week display */}
            <div className="-mr-px hidden grid-cols-7 divide-x divide-gray-100 border-r border-gray-100 text-sm leading-6 text-gray-500 sm:grid">
              <div className="col-end-1 w-14" />
              {DayNames.map(
                (day, idx) =>
                  daysInWeek[idx] && (
                    <div
                      key={day}
                      className="flex items-center justify-center py-3"
                    >
                      <span
                        className={`${
                          isToday(daysInWeek[idx]) ? 'flex items-baseline' : ''
                        }`}
                      >
                        {day.substring(0, 3)}{' '}
                        <span
                          className={`items-center justify-center font-semibold ${
                            isToday(daysInWeek[idx])
                              ? 'ml-1.5 flex h-8 w-8 text-white rounded-full bg-indigo-600 '
                              : 'text-gray-900'
                          }`}
                        >
                          {daysInWeek[idx]?.getDate() === 1
                            ? `${daysInWeek[idx]?.getDate()}/${
                                daysInWeek[idx]?.getMonth() + 1
                              }`
                            : daysInWeek[idx]?.getDate()}
                        </span>
                      </span>
                    </div>
                  )
              )}
            </div>
          </div>
          <div className="flex flex-auto">
            <div className="sticky left-0 z-10 w-14 flex-none bg-white ring-1 ring-gray-100" />
            <div className="grid flex-auto grid-cols-1 grid-rows-1">
              {/* Horizontal lines */}
              <div
                className="col-start-1 col-end-2 row-start-1 grid divide-y divide-gray-100"
                style={{ gridTemplateRows: 'repeat(48, minmax(3.5rem, 1fr))' }}
              >
                <div className="row-end-1 h-7"></div>
                {DayTime.map((day) => (
                  <Fragment key={day}>
                    <div>
                      <div className="sticky left-0 z-20 -ml-14 -mt-2.5 w-14 pr-2 text-right text-xs leading-5 text-gray-400">
                        {day}
                      </div>
                    </div>
                    <div />
                  </Fragment>
                ))}
              </div>

              {/* Vertical lines */}
              <div className="col-start-1 col-end-2 row-start-1 hidden grid-cols-7 grid-rows-1 divide-x divide-gray-100 sm:grid sm:grid-cols-7">
                <div className="col-start-1 row-span-full" />
                <div className="col-start-2 row-span-full" />
                <div className="col-start-3 row-span-full" />
                <div className="col-start-4 row-span-full" />
                <div className="col-start-5 row-span-full" />
                <div className="col-start-6 row-span-full" />
                <div className="col-start-7 row-span-full" />
                <div className="col-start-8 row-span-full w-8" />
              </div>

              {/* Events */}
              <ol
                className="col-start-1 col-end-2 row-start-1 grid grid-cols-1 sm:grid-cols-7 sm:pr-8"
                style={{
                  gridTemplateRows: '1.75rem repeat(288, minmax(0, 1fr)) auto',
                }}
              >
                {weeklyEventDisplay.map((event, idx) => {
                  const startHour = Math.floor(event.startHour);
                  const startMinute = (event.startHour * 60) % 60;
                  const startRow =
                    (2 * startHour + Math.ceil(startMinute / 30)) * 6 + 2;

                  const endHour = Math.floor(event.endHour);
                  const endMinute = (event.endHour * 60) % 60;
                  const span =
                    (2 * (Math.ceil(endHour) - Math.floor(startHour)) +
                      Math.ceil(endMinute / 30) -
                      Math.ceil(startMinute / 30)) *
                    6;
                  return (
                    <Fragment key={idx}>
                      <li
                        className={`relative mt-px flex ${event.colStartStyling} hidden sm:block`}
                        style={{
                          gridRow: `${startRow} / span ${span}`,
                          width: event.displayWidth || '100%', // Add width
                          marginLeft: `${event.offset || 0}%`, // Add offset
                        }}
                      >
                        <button className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg bg-blue-50 p-2 text-xs leading-5 hover:bg-blue-100">
                          <p className="order-1 font-semibold text-blue-700 text-left">
                            {event.title}
                          </p>
                        </button>
                      </li>
                      <li
                        className={`relative mt-px flex block sm:hidden ${
                          isSameDate(event.start, currentDate) ? '' : 'hidden'
                        }`}
                        style={{
                          gridRow: `${startRow} / span ${span}`,
                          width: event.displayWidth || '100%', // Add width
                          marginLeft: `${event.offset || 0}%`, // Add offset
                        }}
                      >
                        <button className="group absolute inset-1 flex flex-col overflow-y-auto rounded-lg bg-blue-50 p-2 text-xs leading-5 hover:bg-blue-100">
                          <p className="order-1 font-semibold text-blue-700 text-left">
                            {event.title}
                          </p>
                        </button>
                      </li>
                    </Fragment>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

type PropsType = {
  today: Date;
  startOfWeek: Date;
  events: CalendarEvent[];
  currentDate: Date;
  onUpdateCurrentDate: (date: Date) => void;
};
export default WeekView;
